import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { ContextMenuConfigV3, ContextMenuV3Component, IconV3Component } from '@gea/digital-ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';

interface CustomContextMenuConfig<T> extends ContextMenuConfigV3<T> {
  visibleFor: (rowData: any) => boolean;
}

@Component({
  selector: 'app-options-table-menu',
  templateUrl: './options-table-menu.component.html',
  styleUrl: './options-table-menu.component.scss',
  imports: [IconV3Component, TranslateModule, CommonModule, Popover, Tooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionsTableMenuComponent<T> extends ContextMenuV3Component<T> implements OnInit, OnDestroy {
  private cdRef = inject(ChangeDetectorRef);
  private hostEl = inject(ElementRef<HTMLElement>);

  public override config!: CustomContextMenuConfig<T>;

  private removeListeners: Array<() => void> = [];

  setConfig(value: any) {
    if (value && typeof value.items === 'function') {
      const resolved = value.items();
      const filteredItems = (Array.isArray(resolved) ? resolved : []).filter((element: any) => {
        return element.visible(this.rowData);
      });
      value = { ...value, items: filteredItems };
    }
    this.config = value;
  }

  override ngOnInit(): void {
    this.setConfig(this.config);
    super.ngOnInit?.();

    const root = this.hostEl.nativeElement;

    const isInsideHost = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false;
      return root.contains(target);
    };

    const isInsidePopover = (ev: Event): boolean => {
      // p-popover renders as a portal directly on <body>, outside the host.
      // Walk the composed path to detect it.
      const path = ((ev as any).composedPath?.() as EventTarget[]) ?? [];
      return path.some(
        (n) =>
          n instanceof Element &&
          (n.classList.contains('p-popover') ||
            n.classList.contains('popover-wrapper') ||
            n.classList.contains('context-menu-popover') ||
            n.classList.contains('menu-container') ||
            n.classList.contains('menu-item'))
      );
    };

    const captureHandler = (ev: Event) => {
      if (!this.open) return;

      // Clicks inside the host (dots icon) or inside the popover panel are fine
      if (isInsideHost(ev.target) || isInsidePopover(ev)) return;

      // Swallow the event — row click never fires, menu closes
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      ev.preventDefault();

      if (ev.type === 'click') {
        this.popover?.hide();
        this.open = false;
        this.cdRef.detectChanges();
      }
    };

    const types: Array<keyof DocumentEventMap> = ['pointerdown', 'click'];
    for (const type of types) {
      document.addEventListener(type, captureHandler, { capture: true });
      this.removeListeners.push(() => document.removeEventListener(type, captureHandler, true));
    }
  }

  override handleOnShow(event?: Event): void {
    event?.stopPropagation();
    this.open = true;
  }

  override handleTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  override ngOnDestroy(): void {
    this.removeListeners.forEach((off) => off());
    this.removeListeners = [];
    super.ngOnDestroy?.();
  }
}
